import { ATTRIBUTE_LIMITS, WORKFLOW_LIMITS, type AttributeObject } from "@sagot/constants";

/** One defined attribute with the value a conversation, a visitor or a lead holds (`GET …/attributes`). */
export interface AttributeItem {
  object: string;
  key: string;
  label: string;
  type: string;
  options: string[] | null;
  value: unknown;
}

/** What an input holds while the person edits: text, or the chosen options of a list attribute. */
export type DraftValue = string | string[];
export type Draft = Readonly<Record<string, DraftValue>>;

/** A value the API accepts for one attribute: text, number, boolean, a list of options, or `null` to clear it. */
export type AttributeInput = boolean | number | string | string[] | null;
export type AttributePatches = Partial<Record<AttributeObject, Record<string, AttributeInput>>>;

export const itemId = (item: Pick<AttributeItem, "object" | "key">): string => `${item.object}.${item.key}`;

/** Whether the attribute is a list with a fixed set of choices (chips), as opposed to free comma-separated text. */
export const hasChoices = (item: AttributeItem): item is AttributeItem & { options: string[] } => item.type === "list" && item.options !== null && item.options.length > 0;

/** The stored value as an input shows it. */
export function draftValue(item: AttributeItem): DraftValue {
  const { value } = item;
  if (value === null || value === undefined) return hasChoices(item) ? [] : "";
  if (Array.isArray(value)) {
    const words = value.map(String);
    return hasChoices(item) ? words : words.join(", ");
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

export function draftOf(items: readonly AttributeItem[]): Draft {
  return Object.fromEntries(items.map((item) => [itemId(item), draftValue(item)]));
}

const sameDraft = (a: DraftValue, b: DraftValue): boolean => (Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((word, index) => word === b[index]) : a === b);

/** The ids of the attributes whose input differs from what is stored. */
export function changedIds(items: readonly AttributeItem[], draft: Draft): string[] {
  return items.filter((item) => !sameDraft(draft[itemId(item)] ?? draftValue(item), draftValue(item))).map(itemId);
}

/** What one input sends: an empty input clears the attribute, a boolean is a boolean, a list of choices a list, the rest text. */
export function inputOf(item: AttributeItem, raw: DraftValue): AttributeInput {
  if (Array.isArray(raw)) return raw.length === 0 ? null : raw;
  const text = raw.trim();
  if (text === "") return null;
  if (item.type === "bool") return text === "true";
  return text;
}

/** The request bodies, per object, for the attributes that changed. */
export function patchesOf(items: readonly AttributeItem[], draft: Draft): AttributePatches {
  const changed = new Set(changedIds(items, draft));
  const patches: AttributePatches = {};
  for (const item of items) {
    if (!changed.has(itemId(item))) continue;
    const object = item.object as AttributeObject;
    patches[object] = { ...patches[object], [item.key]: inputOf(item, draft[itemId(item)] ?? "") };
  }
  return patches;
}

/** The objects of a list of items, in the order they first appear. */
export function objectsOf(items: readonly AttributeItem[]): AttributeObject[] {
  return [...new Set(items.map((item) => item.object as AttributeObject))];
}

/** Longest text an input takes (backend `ATTRIBUTE_VALUE_MAX`). */
export const ATTRIBUTE_INPUT_MAX = WORKFLOW_LIMITS.attributeValue;
/** A list attribute holds at most this many choices of this length. */
export const ATTRIBUTE_LIST_LIMITS = { items: 20, option: ATTRIBUTE_LIMITS.option } as const;
